import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import World from 'components/World';
import {logEvent} from 'utils/firebase/analytics';
import {
  getResourceIDFromLocation,
  encodeForHTML,
} from 'utils/helpers';
import {
  updateTextOverlay,
  removeTextOverlay,
  clearTextOverlays,
  setLabelDisplay,
  clearLevelLoading,
} from 'store/world/actions';
import {
  syncWorldPeerState,
  sendWorldMessage,
  syncPeerSate,
  sendLocation,
  sendAppearance,
} from 'store/communication/actions/controlActions';
import {
  setAvatarLoading,
  forceBroadcast,
} from 'store/appearance';
import {setMeTransform} from 'store/communication/actions/stateActions';
import {clearViewboards, registerViewboards} from 'store/viewboard';
import {
  shouldUpdateTransformReduxState,
  shouldUpdateTransformUnity,
  convertMessageToTransform,
} from 'utils/World/Transform';
import worldStyle from 'style/3d';
import {LEVEL_LOAD} from 'utils/firebase/analytics.config';

const MeetingWorld = () => {
  const [firstLoad, setFirstLoad] = useState(true);
  const dispatch = useDispatch();
  const location = useSelector((state) => state.world.location);
  const teleportToPeer = useSelector((state) => state.world.teleportToPeer);
  const showFloatingNames = useSelector(
    (state) => state.world.showFloatingNames,
  );
  const loadStarted = useSelector((state) => state.world.loadStarted);

  const lowPowerMode = useSelector(
    (state) => state.meeting.settings.lowPowerMode,
  );
  const meTransformData = useSelector(
    (state) => state.communication.me.transform,
  );
  const peers = useSelector((state) => state.communication.peers);
  const peerId = useSelector((state) => state.communication.me.peerId);
  const avatarId = useSelector((state) => state.appearance.avatarId);
  const skinToneHex = useSelector((state) => state.appearance.skinToneHex);
  const [floatingNames, setFloatingNames] = useState(showFloatingNames);
  useEffect(() => {
    setFloatingNames(showFloatingNames);
  }, [showFloatingNames]);

  const forceAppearanceBroadcast = useSelector(
    (state) => state.appearance.forceBroadcast,
  );
  useEffect(() => {
    if (forceAppearanceBroadcast) {
      // Avatar data (avatarId/skinToneHex) is already in Redux state.
      // RoomClient broadcasts it directly via protoo — no Unity roundtrip needed.
      console.log('[MP_RN] MeetingWorld -> syncWorldPeerState: trigger=forceAppearanceBroadcast');
      dispatch(syncWorldPeerState());
      dispatch(forceBroadcast(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceAppearanceBroadcast]);

  const locations = useSelector((state) => state.meeting.properties.locations);
  const [locationResourceID, setLocationResourceID] = useState(
    getResourceIDFromLocation(location, locations),
  );
  useEffect(() => {
    if (location) {
      setLocationResourceID(getResourceIDFromLocation(location, locations));
    }
  }, [location, locations]);

  const onLoadComplete = () => {
    console.log('[MP_RN] MeetingWorld.onLoadComplete');
    setFirstLoad(true);
    dispatch(clearTextOverlays());
    World.runCommand('set_overlay_z_index_delta 10000000');
    World.runCommand('set_overlay_distance_delta 0.0005');
    dispatch(sendLocation(location));
    // set the loading views
    dispatch(setAvatarLoading(true));
    if (teleportToPeer) {
      World.runCommand(`teleport_user ${teleportToPeer}`);
    }

    dispatch(clearViewboards());
    dispatch(registerViewboards(locationResourceID));
    if (loadStarted !== -1) {
      logEvent(LEVEL_LOAD, {
        level: location,
        duration: Date.now() - loadStarted,
      });
      dispatch(clearLevelLoading());
    }
    // Send the local avatar to Unity. Unity wipes avatar state on every
    // scene change (UserController.Reset), so we must re-send after each
    // scene load. The load_avatwin_avatar shell command builds an
    // AvatarInfo with avaTwinAvatarId set and routes through set_avatar,
    // which lands in AvatarManager.LoadAvatar's isAvaTwin branch. That
    // branch emits the route-'p' 'avatarReady' back to us, which flips
    // setAvatarLoading(false) and enables the Broadcast Appearance button.
    if (peerId) {
      World.runCommand(`set_external_user_id ${peerId}`);
    }
    if (avatarId) {
      const skin = skinToneHex || '';
      World.runCommand(`load_avatwin_avatar runtime.user.local ${avatarId} ${skin}`);
    }
    // sync peer state with all other existing peers
    console.log('[MP_RN] MeetingWorld -> syncWorldPeerState: trigger=onLoadComplete');
    dispatch(syncWorldPeerState());
  };

  const onLegacyFromWorld = (message) => {
    const {route, payload} = message;

    if (route === 't') {
      const overlayAction = (payload || '').split('|')[0];
      console.log('[WEBGL_DBG] MeetingWorld: legacy route=t overlay action=' + (overlayAction));
      handleOverlayMessage(payload);
    } else if (route === 'n') {
      console.log('[WEBGL_DBG] MeetingWorld: legacy route=n label=' + payload);
      handleLabelMessage(payload);
    } else if (route === 'p') {
      const player_variables = payload.split('|');
      switch (player_variables[0]) {
        case 'avatarReady': {
          console.log('[WEBGL_DBG] MeetingWorld: legacy route=p avatarReady received');
          const readyAvatarId = player_variables[1] || '';
          const readySkinTone = player_variables[2] || '';
          console.log('[MP_RN] MeetingWorld.avatarReady: avatarId=' + readyAvatarId + ' skinTone=' + readySkinTone);
          // Unity signals avatar loaded: avatarReady|<avatarId>|<skinToneHex>
          // Avatar is ready — hide loading indicator
          dispatch(setAvatarLoading(false));
          // Broadcast peer state on first load so others see our avatar
          if (firstLoad) {
            setFirstLoad(false);
            console.log('[MP_RN] MeetingWorld -> syncWorldPeerState: trigger=avatarReady');
            dispatch(syncWorldPeerState());
          }
          // Auto-publish appearance so remote peers receive avatarId + skinToneHex
          // without requiring the user to tap "Broadcast Appearance".
          if (readyAvatarId) {
            console.log('[MP_RN] MeetingWorld -> sendAppearance: avatarId=' + readyAvatarId + ' skinTone=' + readySkinTone);
            dispatch(sendAppearance(readyAvatarId, readySkinTone));
          }
          break;
        }
        default:
          console.warn(
            '[WEBGL_DBG] MeetingWorld: legacy route=p unhandled var=' + player_variables[0],
          );
      }
    } else {
      console.log('[WEBGL_DBG] MeetingWorld: forwarding unknown route to peers route=' + (message && message.route));
      dispatch(sendWorldMessage(message));
    }

    if (route === 'd') {
      const peerKey = payload;
      console.log('[WEBGL_DBG] MeetingWorld: legacy route=d peerAppearanceReq peerKey=' + peerKey);
      // REQUEST REMOTE USER APPEARANCE
      dispatch(syncPeerSate(payload));
    }
  };

  const onTransformFromWorld = (message) => {
    const newTransformData = convertMessageToTransform(message?.payload);
    if (
      shouldUpdateTransformReduxState({
        oldTransformData: meTransformData,
        newTransformData,
      })
    ) {
      dispatch(setMeTransform(message));
    }

    if (
      shouldUpdateTransformUnity({
        oldTransformData: meTransformData,
        newTransformData,
      })
    ) {
      dispatch(sendWorldMessage(message));
    }
  };

  const getLabelInfo = (label) => {
    if (!label || label === '') {
      return null;
    }

    const sanitizedLabel = label.replace(/_([0-9]+)?/g, '');

    for (const loc of locations) {
      if (loc.id === location) {
        const locLabels = loc.labels;
        let info = null;
        if (typeof locLabels !== 'undefined' && locLabels) {
          info = locLabels[label] || locLabels[sanitizedLabel] || null;
        }
        return info;
      }
    }

    return null;
  };

  const handleLabelMessage = (payload) => {
    const labelInfo = getLabelInfo(payload);

    dispatch(setLabelDisplay(labelInfo));
  };

  const handleOverlayMessage = (payload) => {
    const parts = payload.split('|');
    const action = parts[0];
    switch (action) {
      case 'set': {
        if (parts.length < 6) {
          console.warn('TextOverlay.Set was incorrectly formatted: ', payload);
          break;
        }
        const id = parts[1];
        const xPos = parseFloat(parts[2]);
        const yPos = parseFloat(parts[3]);
        let text = encodeForHTML(parts[4]);
        const zIdx = parseInt(parts[5], 10);
        const labelStyle = parts[6] ?? 'name'; // Possible values are 'chat', 'name', and 'viewboard', default to 'name'
        const targetData = parts.length >= 8 ? parts[7] : '';

        let targetArray;
        let target;
        let peer;

        // if the text is "..." set it to the user's name instead.
        if (labelStyle === 'name') {
          // check that the targetData exists
          if (targetData !== '') {
            targetArray = targetData.split('.');
            target = targetArray[3];
            peer = peers[target];
          }
          // if the local user's location is the same as the peers location, process the update
          if (peer?.location !== location) {
            break;
          }
          if (text === '...') {
            // get the users nickname from the store
            const displayName =
              typeof peers[target] === 'undefined'
                ? target
                : peers[target].displayName;
            text = encodeForHTML(displayName);
          }
        }
        dispatch(updateTextOverlay(id, text, labelStyle, xPos, yPos, zIdx));
        break;
      }
      case 'remove': {
        const id = parts[1];
        dispatch(removeTextOverlay(id));
        break;
      }
      case 'clear': {
        // No params, just remove all labels
        dispatch(clearTextOverlays());
        break;
      }
      case 'end': {
        // No params, just end frame
        break;
      }
      default: {
        console.warn('[WEBGL_DBG] MeetingWorld: unexpected overlay action payload=' + payload);
        break;
      }
    }
  };

  return location && locationResourceID ? (
    <World
      location={locationResourceID}
      locationFilter={location}
      onLoadComplete={onLoadComplete}
      onTransformFromWorld={onTransformFromWorld}
      onLegacyFromWorld={onLegacyFromWorld}
      lowPower={lowPowerMode}
      showFloatingNames={floatingNames}
      style={worldStyle.world}
    />
  ) : null;
};

export default MeetingWorld;
